import { create } from 'zustand'
import BigNumber from 'bignumber.js'
import { getLastSelectedMarketSymbol, setLastSelectedMarketSymbol } from '@shared/utils'

// 기본 폼 데이터 (mock)
const INITIAL_DATA: BotDcaFormData = {
  entryCount: '6',
  entryMultiple: '1.5',
  entryInterval: '2',
  takeProfitPercent: '1',
  startQuantity: '0.1',
  startQuantitySuffix: '',
  stopLossPercent: '',
  totalQuantity: '7',
  totalQuantitySuffix: '',
  totalAsset: '1,000.00',
  availableAsset: '1,000.00',
}

const INITIAL_ROWS: BotDcaDetailStepRow[] = [
  { step: '', orderSize: '' },
  { step: '', orderSize: '' },
  { step: '', orderSize: '' },
  { step: '', orderSize: '' },
  { step: '', orderSize: '' },
  { step: '', orderSize: '' },
]

const MIN_ROWS = 2

const toText = (value: unknown): string => (value == null ? '' : String(value))
const toLoadedQuantityText = (value: unknown): string => {
  if (value == null) return ''
  const quantity = new BigNumber(String(value))
  if (!quantity.isFinite()) return String(value)
  return quantity.decimalPlaces(8, BigNumber.ROUND_HALF_UP).toString()
}

interface BotFormTradingState {
  selectedSymbol: string
  activeTab: BotDcaTabKey
  side: BotDcaTradeSide
  data: BotDcaFormData
  detailRows: BotDcaDetailStepRow[]
}

interface BotFormOptionsState {
  stopLoss: boolean
  repeat: boolean
  stopAfterLoss: boolean
  hedgeMode: boolean
  hedgePricePercent: string
  hedgeVolume: string
}

interface BotFormActions {
  setSelectedSymbol: (symbol: string) => void
  setActiveTab: (tab: BotDcaTabKey) => void
  setSide: (side: BotDcaTradeSide) => void
  setTradingField: (field: keyof BotDcaFormData, next: unknown) => void
  setDetailRow: (idx: number, field: keyof BotDcaDetailStepRow, next: unknown) => void
  addDetailRow: () => void
  removeDetailRow: () => void
  setOption: <K extends keyof BotFormOptionsState>(
    field: K,
    value: BotFormOptionsState[K],
  ) => void
  loadFromBotData: (raw: DCABotSetData | null) => void
  reset: () => void
}

interface BotFormStore {
  trading: BotFormTradingState
  options: BotFormOptionsState
  actions: BotFormActions
}

const INITIAL_TRADING: BotFormTradingState = {
  selectedSymbol: getLastSelectedMarketSymbol(),
  activeTab: 'simple',
  side: 'sell',
  data: INITIAL_DATA,
  detailRows: INITIAL_ROWS,
}

const INITIAL_OPTIONS: BotFormOptionsState = {
  stopLoss: false,
  repeat: true,
  stopAfterLoss: false,
  hedgeMode: false,
  hedgePricePercent: '0',
  hedgeVolume: '0',
}

export const useBotFormStore = create<BotFormStore>((set, get) => ({
  trading: INITIAL_TRADING,
  options: INITIAL_OPTIONS,
  actions: {
    setSelectedSymbol: symbol => {
      setLastSelectedMarketSymbol(symbol)
      set(state => ({ trading: { ...state.trading, selectedSymbol: symbol } }))
    },

    setActiveTab: tab =>
      set(state => ({ trading: { ...state.trading, activeTab: tab } })),

    setSide: side => set(state => ({ trading: { ...state.trading, side } })),

    // 거래설정 필드 단건 업데이트
    // entryCount 변경 시 detailRows 길이를 같이 맞춰서 간편/상세 진입횟수 공유
    setTradingField: (field, next) => {
      const text = toText(next)
      set(state => {
        const nextData = { ...state.trading.data, [field]: text }
        if (field !== 'entryCount') {
          return { trading: { ...state.trading, data: nextData } }
        }
        const count = Math.max(0, Math.floor(Number(text) || 0))
        const prev = state.trading.detailRows
        let nextRows = prev
        if (count !== prev.length) {
          if (count < prev.length) {
            nextRows = prev.slice(0, Math.max(MIN_ROWS, count))
          } else {
            const fill: BotDcaDetailStepRow[] = Array.from(
              { length: count - prev.length },
              () => ({ step: '', orderSize: '' }),
            )
            nextRows = [...prev, ...fill]
          }
        }
        return { trading: { ...state.trading, data: nextData, detailRows: nextRows } }
      })
    },

    setDetailRow: (idx, field, next) =>
      set(state => ({
        trading: {
          ...state.trading,
          detailRows: state.trading.detailRows.map((row, i) =>
            i === idx ? { ...row, [field]: toText(next) } : row,
          ),
        },
      })),

    addDetailRow: () => {
      const { trading } = get()
      const nextLen = trading.detailRows.length + 1
      set({
        trading: {
          ...trading,
          detailRows: [...trading.detailRows, { step: '', orderSize: '' }],
          data: { ...trading.data, entryCount: String(nextLen) },
        },
      })
    },

    removeDetailRow: () => {
      const { trading } = get()
      if (trading.detailRows.length <= MIN_ROWS) return
      const nextLen = trading.detailRows.length - 1
      set({
        trading: {
          ...trading,
          detailRows: trading.detailRows.slice(0, -1),
          data: { ...trading.data, entryCount: String(nextLen) },
        },
      })
    },

    setOption: (field, value) =>
      set(state => ({ options: { ...state.options, [field]: value } })),

    // 봇 응답 데이터로 폼 전체 복원
    loadFromBotData: raw => {
      // 사용자가 선택한 selectedSymbol / side는 보존한다
      if (!raw) {
        set(state => ({
          trading: {
            ...INITIAL_TRADING,
            selectedSymbol: state.trading.selectedSymbol,
            side: state.trading.side,
          },
          options: INITIAL_OPTIONS,
        }))
        return
      }
      const sim = raw.simple
      const opt = raw.options
      const nextData: BotDcaFormData = {
        ...INITIAL_DATA,
        entryCount: sim ? toText(sim.entryCount) : INITIAL_DATA.entryCount,
        entryMultiple: sim ? toText(sim.entryMultiplier) : INITIAL_DATA.entryMultiple,
        entryInterval: sim ? toText(sim.entryIntervalPercent) : INITIAL_DATA.entryInterval,
        takeProfitPercent:
          raw.takeProfitPercent !== undefined
            ? toText(raw.takeProfitPercent)
            : INITIAL_DATA.takeProfitPercent,
        startQuantity: sim ? toLoadedQuantityText(sim.startOrderSize) : INITIAL_DATA.startQuantity,
        stopLossPercent: opt ? toText(opt.stopLossPercent) : INITIAL_DATA.stopLossPercent,
      }
      const nextRows =
        raw.detail && raw.detail.steps.length > 0
          ? raw.detail.steps.map((s, i) => ({
              step: i === 0 ? '' : toText(s.stepPercent),
              orderSize: toText(s.orderSize),
            }))
          : INITIAL_ROWS
      set(state => ({
        trading: {
          ...state.trading,
          activeTab: raw.setType === 1 ? 'detail' : 'simple',
          data: nextData,
          detailRows: nextRows,
        },
        options: opt
          ? {
              stopLoss: opt.useStopLoss,
              repeat: opt.repeatExecution,
              stopAfterLoss: opt.stopBotAfterStopLoss,
              hedgeMode: opt.useHedgeMode,
              hedgePricePercent: toText(opt.hedge?.pricePercent ?? 0),
              hedgeVolume: toText(opt.hedge?.volume ?? 0),
            }
          : INITIAL_OPTIONS,
      }))
    },

    reset: () => set({ trading: INITIAL_TRADING, options: INITIAL_OPTIONS }),
  },
}))

// Selectors
export const selectBotFormTrading = (s: BotFormStore) => s.trading
export const selectBotFormOptions = (s: BotFormStore) => s.options
export const selectBotFormActions = (s: BotFormStore) => s.actions
